import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'admin-brands:theme';
const DEFAULT_MODE = 'system';
const VALID_MODES = ['light', 'dark', 'system'];

// Page background tokens for <meta name="theme-color"> — mirrored from
// theme.js so mobile browser chrome matches the canvas.
const THEME_COLOR = {
    light: '#F8FAFC',
    dark: '#0F172A'
};

const readInitial = () => {
    if (typeof window === 'undefined') return DEFAULT_MODE;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return VALID_MODES.includes(stored) ? stored : DEFAULT_MODE;
    } catch (_) {
        return DEFAULT_MODE;
    }
};

const prefersDark = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const resolve = (mode, systemDark) => (mode === 'system' ? (systemDark ? 'dark' : 'light') : mode);

const ThemeContext = createContext({
    mode: DEFAULT_MODE,
    resolved: 'light',
    setMode: () => {},
    toggle: () => {}
});

export const ThemeProvider = ({ children }) => {
    const [mode, setModeState] = useState(readInitial);
    const [systemDark, setSystemDark] = useState(prefersDark);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemDark(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch (_) {
            /* storage unavailable (private mode) — harmless */
        }
    }, [mode]);

    const resolved = resolve(mode, systemDark);

    // Drive <html data-theme> + color-scheme + <meta name="theme-color">.
    // Keeping this in one effect (rather than three) avoids flicker on toggle.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        root.setAttribute('data-theme', resolved);
        root.style.colorScheme = resolved;
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'theme-color');
            document.head.appendChild(meta);
        }
        meta.setAttribute('content', THEME_COLOR[resolved] || THEME_COLOR.light);
    }, [resolved]);

    const value = useMemo(
        () => ({
            mode,
            resolved,
            setMode: (next) => setModeState(VALID_MODES.includes(next) ? next : DEFAULT_MODE),
            toggle: () => setModeState((prev) => (resolve(prev, systemDark) === 'dark' ? 'light' : 'dark'))
        }),
        [mode, resolved, systemDark]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
